import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Heart, ExternalLink, Users, Globe, ArrowRight, Share2, Award, Droplets, GraduationCap, ShieldCheck } from 'lucide-react';
import { PageTransition } from '../components/PageTransition';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
export function MillionMovementPage() {
  return <PageTransition data-id="element-482">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-[70vh] flex items-center" data-id="element-483">
        <div className="absolute inset-0" data-id="element-484">
          <img src="https://cdn.magicpatterns.com/uploads/qHJ8caWqcsCxLWFqzpKR82/jwxld-12-scaled.webp" alt="Jeremy Willet speaking on stage" className="w-full h-full object-cover" data-id="element-485" />
          <div className="absolute inset-0 bg-dark-900/70" data-id="element-486"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-487">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-488">
            <span className="text-gold-500 font-medium tracking-wider uppercase text-sm mb-4 block" data-id="element-489">
              Child Sponsorship
            </span>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-490">
              The Million <span className="text-gold-500" data-id="element-491">Movement</span>
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-492">
              A team of storytellers driving an innovative approach to child
              sponsorship to see one million children sponsored by 2050.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-493">
              <a href="https://themillionmovement.com/" target="_blank" rel="noopener noreferrer" data-id="element-494">
                <Button size="lg" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-496" />} data-id="element-495">
                  Visit Official Website
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-dark-700" data-id="element-497">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-498">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} data-id="element-499">
            <Globe className="w-12 h-12 text-gold-500 mx-auto mb-6" data-id="element-500" />
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-6" data-id="element-501">
              One Million Children by 2050
            </h2>
            <p className="text-lg text-white/80 leading-relaxed mb-6" data-id="element-502">
              Founded by Child Advocate Jeremy Willet, The Million Movement
              produces events and award-winning campaigns that travel on
              nationwide concert tours to find sponsors for children living in
              poverty. We believe in the power of art, child sponsorship, and
              dignity for every child.
            </p>
            <p className="text-lg text-white/80 leading-relaxed mb-6" data-id="element-503">
              To align with the Sustainable Development Goals to eradicate all
              forms of poverty, we partner with the largest Non-Governmental
              Organizations (NGOs) to create custom, first-hand experience
              visual campaigns.
            </p>
            <div className="w-24 h-1 bg-gold-500 mx-auto rounded-full mb-10" data-id="element-504"></div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-505">
              <div className="flex items-center gap-3 px-5 py-3 bg-dark-800 border border-gold-500/30 rounded-lg" data-id="element-506">
                <Award className="w-5 h-5 text-gold-500" data-id="element-507" />
                <span className="text-white/90 text-sm font-medium" data-id="element-508">
                  Bronze Anthem Award — Humanitarian Action & Services
                  Fundraising (2024)
                </span>
              </div>
              <div className="flex items-center gap-3 px-5 py-3 bg-dark-800 border border-gold-500/30 rounded-lg" data-id="element-509">
                <Award className="w-5 h-5 text-gold-500" data-id="element-510" />
                <span className="text-white/90 text-sm font-medium" data-id="element-511">
                  Community Voice Anthem Award (2024)
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Sponsorship Supports */}
      <section className="py-24 bg-dark-800 border-t border-dark-600" data-id="element-512">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-513">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto mb-16" data-id="element-514">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden border border-dark-500 shadow-2xl" data-id="element-515">
              <img src="https://cdn.magicpatterns.com/uploads/5onSWpK5fB6pgy4PvwwtQ5/IMG_0776.jpg" alt="Child sponsorship through The Million Movement" className="w-full h-full object-cover" data-id="element-516" />
            </div>
          </motion.div>

          <div className="text-center mb-16" data-id="element-517">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4" data-id="element-518">
              What Child Sponsorship Supports
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto" data-id="element-519">
              Child sponsorship helps support essential needs for every child
              and family.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-520">
            {[{
            title: 'Clean Water',
            description: 'Access to safe, clean drinking water — the foundation of health and well-being for every child.',
            icon: <Droplets className="w-6 h-6 text-gold-500" data-id="element-521" />
          }, {
            title: 'Nutrition',
            description: 'Proper meals and nutritional support to fuel growing bodies and minds.',
            icon: <Heart className="w-6 h-6 text-gold-500" data-id="element-522" />
          }, {
            title: 'Education',
            description: 'Quality schooling and learning resources that open doors to a brighter future.',
            icon: <GraduationCap className="w-6 h-6 text-gold-500" data-id="element-523" />
          }, {
            title: 'Healthcare & Protection',
            description: 'Medical care and child protection services to keep every child safe and healthy.',
            icon: <ShieldCheck className="w-6 h-6 text-gold-500" data-id="element-524" />
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-525">
                <Card className="p-6 h-full bg-dark-700 border-dark-600 hover:border-gold-500/50 transition-colors" data-id="element-526">
                  <div className="w-12 h-12 rounded-full bg-dark-800 border border-dark-500 flex items-center justify-center mb-4" data-id="element-527">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-white mb-3" data-id="element-528">
                    {item.title}
                  </h3>
                  <p className="text-white/80 text-sm leading-relaxed" data-id="element-529">
                    {item.description}
                  </p>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-dark-700" data-id="element-530">
        <div className="absolute inset-0 bg-gold-600/5" data-id="element-531"></div>
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-532">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="mb-10" data-id="element-533">
            <div className="max-w-sm mx-auto aspect-square rounded-2xl overflow-hidden border border-dark-500 shadow-2xl" data-id="element-534">
              <img src="https://cdn.magicpatterns.com/uploads/nRfzPtWB3GBG8FpKwbncaP/IMG_0553.jpg" alt="Child sponsorship — Sponsor a child today" className="w-full h-full object-cover" data-id="element-535" />
            </div>
          </motion.div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6" data-id="element-536">
            Sponsor a Child Today
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-537">
            Join The Million Movement and help change a child's life through
            sponsorship. Together, we can reach one million children by 2050.
          </p>
          <a href="https://themillionmovement.com/" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-538">
            <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-540" />} data-id="element-539">
              Explore The Million Movement
            </Button>
          </a>
        </div>
      </section>
    </PageTransition>;
}